import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Header from "./components/Header";
import LoginPage from "./LoginPage";
import LandingPage from "./LandingPage";
import HygieneControlPage from "./HygieneControlPage";
import WebshopPage from "./WebshopPage";
import IcleanView from "./pages/IcleanView";
import IcleanViewCalendar from "./pages/IcleanViewCalendar";
import SidebarMenu from "./components/SidebarMenu";
import DeviceRegistration from "./pages/DeviceRegistration";
import MobileCleanerApp from "./mobile/MobileCleanerApp";
import SimulatorPage from "./pages/SimulatorPage";

function useCleanerMobile(user) {
  const [mobile, setMobile] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = () => setMobile(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return user?.type === "cleaner" && mobile;
}

// Full iClean dashboard app — shown at /app
const IcleanApp = ({ user, setUser, selectedCompany, setSelectedCompany }) => {
  const cleanerMobile = useCleanerMobile(user);

  const [editMode, setEditMode] = useState(false);
  const [readerMode, setReaderMode] = useState(false);
  const [editLoctagMode, setEditLoctagMode] = useState(false);
  const [mopMode, setMopMode] = useState(false);
  const [addMode, setAddMode] = useState(false);
  const [drawMode, setDrawMode] = useState(false);
  const [cleanRouteMode, setCleanRouteMode] = useState(false);
  const [lifeViewMode, setLifeViewMode] = useState(true);
  const [calendarMode, setCalendarMode] = useState(false);
  const [locationSettingsMode, setLocationSettingsMode] = useState(false);
  const [usersMode, setUsersMode] = useState(false);
  const [mobilMode, setMobilMode] = useState(false);
  const [mobilSubMode, setMobilSubMode] = useState(null);
  const [registrationDevice, setRegistrationDevice] = useState(null);
  const [isReading, setIsReading] = useState(false);
  const [registrationHandlers, setRegistrationHandlers] = useState({});
  const [rootLocations, setRootLocations] = useState([]);
  const [activeRootLocationId, setActiveRootLocationIdRaw] = useState(null);

  useEffect(() => {
    if (!user || user.type !== "cleaner") return;
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (isMobile) {
      setMobilMode(true);
      setMobilSubMode((prev) => prev || "clean-route");
    }
  }, [user?.user_id, user?.id, user?.type]);

  useEffect(() => {
    const currentUserId = user?.user_id || user?.id;
    if (!currentUserId || !selectedCompany?.id) {
      setRootLocations([]);
      setActiveRootLocationIdRaw(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [locRes, settingsRes] = await Promise.all([
          fetch(`/locations?company_id=${selectedCompany.id}`, { credentials: "include" }),
          fetch(`/canvas-settings?user_id=${currentUserId}&company_id=${selectedCompany.id}`, { credentials: "include" }),
        ]);
        const locData = await locRes.json();
        if (cancelled) return;

        const roots = (locData.locations || []).filter((l) => l.is_root);
        setRootLocations(roots);

        let stored = null;
        try {
          const settingsData = await settingsRes.json();
          stored = settingsData?.settings?.active_root_location_id ?? null;
        } catch (e) {
          console.warn("canvas-settings parse hiba (nem kritikus):", e);
        }
        const validStored = stored && roots.some((r) => String(r.id) === String(stored)) ? stored : null;
        const valid = validStored ?? (roots.length === 1 ? roots[0].id : null);
        setActiveRootLocationIdRaw(valid);
      } catch (err) {
        console.error("root locations betöltési hiba:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.user_id, user?.id, selectedCompany?.id]);

  const setActiveRootLocationId = (newId) => {
    setActiveRootLocationIdRaw(newId);
    const currentUserId = user?.user_id || user?.id;
    if (!currentUserId || !selectedCompany?.id) return;
    fetch(`/canvas-settings`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        user_id: currentUserId,
        company_id: selectedCompany.id,
        active_root_location_id: newId,
      }),
    }).catch((err) => console.error("canvas-settings mentés hiba:", err));
  };

  const showMobileCalendar = mobilMode && mobilSubMode === "calendar";
  const showMobileCleanRoute = mobilMode && mobilSubMode === "clean-route";

  const icleanViewProps = {
    selectedCompanyId: selectedCompany?.id,
    editMode, setEditMode,
    readerMode, setReaderMode,
    editLoctagMode, setEditLoctagMode,
    addMode, setAddMode,
    mopMode, setMopMode,
    drawMode, setDrawMode,
    cleanRouteMode, setCleanRouteMode,
    lifeViewMode, setLifeViewMode,
    locationSettingsMode, setLocationSettingsMode,
    usersMode, setUsersMode,
    user,
    mobilMode,
    mobilSubMode,
    mobileCleanRouteActive: showMobileCleanRoute,
    registrationDevice, setRegistrationDevice,
    isReading, setIsReading,
    onSetRegistrationHandlers: setRegistrationHandlers,
    activeRootLocationId,
    setActiveRootLocationId,
  };
  const rightPanelOpen =
    !mobilMode &&
    (readerMode || editLoctagMode || locationSettingsMode || usersMode || cleanRouteMode || lifeViewMode || calendarMode);

  if (cleanerMobile) {
    return (
      <MobileCleanerApp
        user={user}
        setUser={setUser}
        selectedCompany={selectedCompany}
      />
    );
  }

  return (
    <div className="flex flex-col h-screen bg-slate-100">
      <Header
        user={user}
        setUser={setUser}
        selectedCompany={selectedCompany}
        setSelectedCompany={setSelectedCompany}
        rootLocations={rootLocations}
        activeRootLocationId={activeRootLocationId}
        setActiveRootLocationId={setActiveRootLocationId}
        rightPanelOpen={rightPanelOpen}
      />
      <div className="flex flex-1 overflow-hidden pt-16">
        <SidebarMenu
          user={user}
          selectedCompany={selectedCompany}
          editMode={editMode} setEditMode={setEditMode}
          readerMode={readerMode} setReaderMode={setReaderMode}
          setMopMode={setMopMode}
          editLoctagMode={editLoctagMode} setEditLoctagMode={setEditLoctagMode}
          addMode={addMode} setAddMode={setAddMode}
          drawMode={drawMode} setDrawMode={setDrawMode}
          cleanRouteMode={cleanRouteMode} setCleanRouteMode={setCleanRouteMode}
          lifeViewMode={lifeViewMode} setLifeViewMode={setLifeViewMode}
          calendarMode={calendarMode} setCalendarMode={setCalendarMode}
          locationSettingsMode={locationSettingsMode} setLocationSettingsMode={setLocationSettingsMode}
          usersMode={usersMode} setUsersMode={setUsersMode}
          mobilMode={mobilMode} setMobilMode={setMobilMode}
          mobilSubMode={mobilSubMode} setMobilSubMode={setMobilSubMode}
          onOpenDeviceSelector={registrationHandlers.onOpenDeviceSelector}
          registrationDevice={registrationDevice}
          isReading={isReading}
          onStartReading={registrationHandlers.onStartReading}
          onStopReading={registrationHandlers.onStopReading}
        />
        <main className="flex-1 relative overflow-hidden">
          {calendarMode && !mobilMode ? (
            <IcleanViewCalendar
              calendarMode={calendarMode}
              selectedCompanyId={selectedCompany?.id}
              rootLocations={rootLocations}
              activeRootLocationId={activeRootLocationId}
            />
          ) : showMobileCalendar ? (
            <IcleanViewCalendar
              calendarMode
              selectedCompanyId={selectedCompany?.id}
              rootLocations={rootLocations}
              activeRootLocationId={activeRootLocationId}
            />
          ) : (
            <Routes>
              <Route index element={<IcleanView {...icleanViewProps} />} />
              <Route path="iclean" element={<IcleanView {...icleanViewProps} />} />
              <Route path="*" element={<IcleanView {...icleanViewProps} />} />
            </Routes>
          )}
        </main>
      </div>
    </div>
  );
};

const App = () => {
  const [user, setUser] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const needsProfileCompletion = !!user && !user.profile_completed;

  useEffect(() => {
    const syncSession = async () => {
      try {
        const response = await fetch("/auth/userinfo", { credentials: "include" });
        const data = await response.json();
        if (data.success && data.user) {
          setUser(data.user);
          localStorage.setItem("user", JSON.stringify(data.user));
          setSelectedCompany(
            data.user.company ||
            data.user.selectedCompany ||
            (data.user.companies && data.user.companies[0]) ||
            null
          );
          return;
        }
      } catch (err) {
        console.warn("Session sync hiba:", err);
      }
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        localStorage.removeItem("user");
        setUser(null);
        setSelectedCompany(null);
      }
    };
    syncSession();
  }, []);

  useEffect(() => {
    if (!user) return;
    const ping = async () => {
      try {
        const response = await fetch("/auth/userinfo", { credentials: "include" });
        const data = await response.json();
        if (!data.success) {
          localStorage.removeItem("user");
          setUser(null);
        }
      } catch (err) {
        // ignore
      }
    };
    ping();
    const interval = setInterval(ping, 30000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    if (!user) {
      setSelectedCompany(null);
      return;
    }
    const nextCompany =
      user.company ||
      user.selectedCompany ||
      (user.companies && user.companies[0]) ||
      null;
    setSelectedCompany(nextCompany);
  }, [user]);

  return (
    <Router>
      <Routes>
        {/* Public TraceMop Hygiene Control landing page */}
        <Route path="/hygiene-control" element={<HygieneControlPage />} />
        <Route path="/hygiene.control" element={<HygieneControlPage />} />
        <Route path="/hygienecontrol" element={<HygieneControlPage />} />

        {/* Not logged in: always coming soon */}
        <Route
          path="*"
          element={!user || needsProfileCompletion ? <LoginPage setUser={setUser} /> : <Navigate to="/" replace />}
        />

        {/* Logged in root: landing page */}
        <Route
          path="/"
          element={!user || needsProfileCompletion ? <LoginPage setUser={setUser} /> : <LandingPage user={user} setUser={setUser} />}
        />

        {/* Logged in: webshop */}
        <Route
          path="/shop"
          element={!user || needsProfileCompletion ? <LoginPage setUser={setUser} /> : <WebshopPage user={user} setUser={setUser} />}
        />

        {/* Logged in: device registration */}
        <Route
          path="/deviceregistration/:token"
          element={!user || needsProfileCompletion ? <LoginPage setUser={setUser} /> : <DeviceRegistration user={user} />}
        />

        <Route
          path="/simulator"
          element={
            !user || needsProfileCompletion ? <LoginPage setUser={setUser} /> :
            user.is_iclean_admin ? <SimulatorPage user={user} setUser={setUser} /> : <Navigate to="/app" replace />
          }
        />

        {/* Logged in: full dashboard app */}
        <Route
          path="/app/*"
          element={
            !user ? <LoginPage setUser={setUser} /> : (
            needsProfileCompletion ? <LoginPage setUser={setUser} /> : (
              <IcleanApp
                user={user}
                setUser={setUser}
                selectedCompany={selectedCompany}
                setSelectedCompany={setSelectedCompany}
              />
            )
            )
          }
        />
      </Routes>
    </Router>
  );
};

export default App;
